import { db } from "../system/firebase/index";
import {
    Timestamp,
    addDoc,
    collection,
    doc,
    deleteDoc,
    getDoc,
    getDocs,
    query,
    setDoc,
    where,
    updateDoc,
} from "firebase/firestore";

class Model {
    static table = "";
    static subtables = [];

    static fieldAddedBy = false;
    static fieldAddedDate = false;
    static fieldEditedBy = false;
    static fieldEditedDate = false;

    static fieldMetadata = {};

    /**
     * Get the Firebase collection path of this Model
     *
     * @returns {string} - The collection path
     */
    static getCollectionPath() {
        return this.subtables.length > 0
            ? this.table + "/" + this.subtables.join("/")
            : this.table;
    }

    /**
     * Retrieve a Model by its ID.
     *
     * @param {string} id - The ID of the Model to retrieve.
     *
     * @returns {Promise<Model|null>} - A promise that resolves to the retrieved Model, or null if not found.
     */
    static async find(id) {
        // ignore empty ids (undefined, null, "", etc) - allows for simpler chaining
        if (!id) {
            return null;
        }

        const document = await getDoc(doc(db, this.getCollectionPath(), id));

        if (document.exists()) {
            return this.fromFirestore(document);
        } else {
            return null;
        }
    }

    /**
     * Retrieve all Models where a field matches a value.
     *
     * @param {string} field - The field to match.
     * @param {string} operator - The operator to use for the match.
     * @param {string} value - The value to match.
     *
     * @returns {Promise<Model[]>} - A promise that resolves to an array of Models.
     *
     * @example
     * const items = await Model.where("active", "==", true);
     */
    static async where(field, operator, value) {
        const querySnapshot = await getDocs(
            query(
                collection(db, this.getCollectionPath()),
                where(field, operator, value)
            )
        );

        const items = querySnapshot.docs.map((document) => {
            return this.fromFirestore(document);
        });

        return items;
    }

    /**
     * Create a new Model from a Firestore document.
     *
     * @param {DocumentSnapshot} document - The Firestore document to create the Model from.
     *
     * @returns {Model} - The new Model.
     */
    static fromFirestore(document) {
        const data = document.data();
        const item = new this();

        // Iterate through the class properties and assign them
        // TODO: Could this also apply defaults / parsing? (maybe would need an object of types for each field, or a method for each field, etc)
        Object.keys(item).forEach((key) => {
            if (key === "id") {
                item[key] = document.id;
            } else {
                item[key] = data[key] ?? null;
            }
        });

        return item;
    }

    /**
     * Create a new Model from a plain JS object.
     *
     * @param {Object} obj - The object to create the Model from.
     *
     * @returns {Model} - The new Model.
     */
    static fromObject(obj) {
        const item = new this();

        // Iterate through the class properties and assign them
        Object.keys(item).forEach((key) => {
            item[key] = obj[key] ?? null;
        });

        return item;
    }

    /**
     * Convert the Model to a plain JS object (without functions).
     *
     * @returns {Object} - The plain JS object.
     */
    toObject() {
        return Object.assign({}, this);
    }

    /**
     * Save the Model to Firestore - this will update an existing document or create a new one
     *
     * @param {Object} currentUser - The currently authenticated user.
     *
     * @returns {Promise} - A promise that resolves when the save is complete.
     */
    async saveToFirestore(currentUser) {
        // FIXME: TODO: LATER: move to cloud functions!
        //  - should really be there to begin with, but need a good way to share the model definitions

        // create a UTC firebase timestamp
        const timestamp = Timestamp.now();

        // always update the "edited" properties (if they exist)
        if (this.constructor.fieldEditedBy) {
            this[this.constructor.fieldEditedBy] = currentUser.uid;
        }
        if (this.constructor.fieldEditedDate) {
            this[this.constructor.fieldEditedDate] = timestamp;
        }

        if (this.id) {
            // NOTE: this will annoyingly return undefined, rather than the updated document reference
            return setDoc(
                doc(db, this.constructor.getCollectionPath(), this.id),
                this.toObject()
            );
        } else {
            // also update the "added" properties (if they exist)
            if (this.constructor.fieldAddedBy) {
                this[this.constructor.fieldAddedBy] = currentUser.uid;
            }
            if (this.constructor.fieldAddedDate) {
                this[this.constructor.fieldAddedDate] = timestamp;
            }

            return addDoc(
                collection(db, this.constructor.getCollectionPath()),
                this.toObject()
            );
        }
    }

    /**
     * Update a single field in the Model and save it to Firestore.
     * 
     * @param {String} name - The name of the field to update.
     * @param {any} value - The new value of the field.
     * 
     * @returns {boolean} - Successful?
     */
    async updateFirestoreField(name, value) {
        // TODO: should I also update fieldEditedBy and fieldEditedDate?

        if (!this.id) {
            return false;
        }

        // update ONLY this field
        try {
            await updateDoc(
                doc(db, this.constructor.getCollectionPath(), this.id),
                {
                    [name]: value,
                }
            );
    
            // update the local object
            this[name] = value;

            return true;
        } catch (error) {
            console.log("Model.updateFirestoreField() error", error);

            return false;
        }
    }

    /**
     * Delete the Model from Firestore
     *
     * @returns {boolean|Promise<void>} - A promise that resolves when the deletion is complete (or false if missing). The promise value will be "undefined", and will resolve whether or not a document actually existed.
     */
    async deleteFromFirestore() {
        if (!this.id) return false;

        return deleteDoc(
            doc(db, this.constructor.getCollectionPath(), this.id)
        );
    }

    /**
     * Return whether the Model has any validation errors.
     *
     * @returns {boolean} - Whether the Model has any errors.
     */
    hasValidationErrors() {
        return Object.keys(this.validationErrors).length > 0;
    }
}

export default Model;
